import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';
import { debounce } from '@chatwoot/utils';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

// Initialize helpMeCount from localStorage to persist across page refreshes
// HELPME_CACHE_FIX: retired. The old localStorage count was not account-scoped
// and went stale, showing wrong/leaked numbers. The API helpme_count is truth.
const getStoredHelpMeCount = () => 0;

const state = {
  mineCount: 0,
  unAssignedCount: 0,
  totalCount: 0,
  allCount: 0,
  helpMeCount: 0, // HELPME_CACHE_FIX: never seed from the (unscoped, stale) localStorage cache; the API is the source of truth
};

export const getters = {
  getStats: $state => $state,
};

// Create a debounced version of the actual API call function
const fetchMetaData = async (commit, params) => {
  try {
    const response = await ConversationApi.meta(params);
    const {
      data: { meta },
    } = response;
    commit(types.SET_CONV_TAB_META, meta);
  } catch (error) {
    // ignore
  }
};

const debouncedFetchMetaData = debounce(fetchMetaData, 500, false, 1000);
const longDebouncedFetchMetaData = debounce(fetchMetaData, 500, false, 5000);

// Fetch helpme count using the same base params as the current list (scoped HelpMe count)
const fetchHelpMeCount = async (commit, baseParams = {}) => {
  try {
    const response = await ConversationApi.meta({
      // Reuse whatever filters are currently active in the list
      inboxId: baseParams.inboxId,
      // Do not force 'all' here; let the caller decide the status
      status: baseParams.status,
      assigneeType: baseParams.assigneeType,
      teamId: baseParams.teamId,
      agentId: baseParams.agentId,
      conversationType: baseParams.conversationType,
    });
    const {
      data: { meta },
    } = response;
    // Use helpme_count from meta instead of all_count
    commit(types.SET_HELPME_COUNT, meta.helpme_count || 0);
  } catch (error) {
    // ignore
  }
};

const debouncedFetchHelpMeCount = debounce(fetchHelpMeCount, 500, false, 1000);

export const actions = {
  get: async ({ commit, state: $state }, params) => {
    if ($state.allCount > 100) {
      longDebouncedFetchMetaData(commit, params);
    } else {
      debouncedFetchMetaData(commit, params);
    }
    // Use the SAME params for helpme count as for the list/meta
    debouncedFetchHelpMeCount(commit, params);
  },
  set({ commit }, meta) {
    commit(types.SET_CONV_TAB_META, meta);
  },
  fetchHelpMeCount: async ({ commit }, params = {}, immediate = false) => {
    // If immediate is true, fetch without debounce (useful for initial page load)
    if (immediate) {
      await fetchHelpMeCount(commit, params);
    } else {
      debouncedFetchHelpMeCount(commit, params);
    }
  },
  // Initialize helpMeCount on page load - fetch immediately to avoid delay
  // Accept optional params so initialization can be scoped if needed
  initializeHelpMeCount: async ({ commit }, params = {}) => {
    await fetchHelpMeCount(commit, params);
  },

  // Immediate fetch for HelpMe updates without debounce for real-time sync
  fetchHelpMeCountImmediate: async ({ commit }, params = {}) => {
    await fetchHelpMeCount(commit, params);
  },
};

export const mutations = {
  [types.SET_CONV_TAB_META](
    $state,
    {
      mine_count: mineCount,
      unassigned_count: unAssignedCount,
      total_count: totalCount,
      all_count: allCount,
    } = {}
  ) {
    $state.mineCount = mineCount;
    $state.totalCount = totalCount;
    $state.allCount = allCount;
    $state.unAssignedCount = unAssignedCount;
    $state.updatedOn = new Date();
  },
  [types.SET_HELPME_COUNT]($state, count) {
    const newCount = count || 0;
    $state.helpMeCount = newCount;
    // Persist count to localStorage so it persists across page refreshes
    try {
      LocalStorage.set(LOCAL_STORAGE_KEYS.HELPME_COUNT, newCount);
    } catch (error) {
      // Ignore localStorage errors
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
